<script>
	import Navbar from '../components/Navbar.svelte';
	import { fetchLog } from '../lib/api.js';
	import { Download } from 'lucide-svelte';

	export let navigate;

	// User-facing filter buckets. Backend `type` values are mapped through
	// CATEGORY_MAP — anything not in the named buckets falls under "maintenance"
	// (Gardener sweeps, discovery cycles, domain manager, combiner, etc.).
	const TYPES = ['all', 'uploads', 'emails', 'mcp', 'maintenance'];
	const CATEGORY_MAP = {
		uploads: ['upload'],
		emails: ['email'],
		mcp: ['mind'],
	};
	const NAMED_TYPES = new Set(['upload', 'email', 'mind']);

	// User-facing phase labels — mirror the backend phase_durations_ms keys.
	// These are deliberately generic and stable across pipeline revisions; the
	// internal stage names (cartographer, describer, decomposer, …) never
	// surface here.
	const PHASE_LABELS = {
		reading: 'Reading',
		mapping: 'Mapping',
		placing: 'Placing',
		visualizing: 'Visualizing',
	};

	// Per-row label for the "type" column. We don't show the raw backend
	// type ('mind', 'discovery', 'gardener') — those are operator-grade.
	function categoryFor(rawType) {
		if (rawType === 'upload') return 'Upload';
		if (rawType === 'email') return 'Email';
		if (rawType === 'mind') return 'MCP';
		return 'Maintenance';
	}

	function matchesActiveType(entry) {
		if (activeType === 'all') return true;
		if (activeType === 'maintenance') return !NAMED_TYPES.has(entry.type);
		const wanted = CATEGORY_MAP[activeType] || [];
		return wanted.includes(entry.type);
	}

	let entries = [];
	let loading = true;
	let activeType = 'all';
	let dateRange = '30d';
	let expandedId = null;
	let expandedGroup = null;
	// Per-row toggle for the raw-JSON operator escape hatch in the detail panel.
	let showTechnicalById = {};

	// Group upload entries by filename; most recent entry drives parent status/summary
	function groupEntries(entries) {
		const groups = [];
		const seen = new Map(); // filename -> group index

		for (const entry of entries) {
			const filename = entry.type === 'upload' && entry.data?.filename;
			if (!filename) {
				groups.push({ type: 'single', entry });
				continue;
			}
			if (seen.has(filename)) {
				groups[seen.get(filename)].entries.push(entry);
			} else {
				seen.set(filename, groups.length);
				groups.push({ type: 'group', filename, entries: [entry] });
			}
		}
		// Guarantee inner entries are strictly DESC by timestamp
		for (const g of groups) {
			if (g.type === 'group') {
				g.entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
			}
		}
		return groups;
	}

	// Inline the filter so Svelte tracks `activeType` as a reactive dependency.
	// Calling matchesActiveType (which closes over activeType) doesn't surface
	// the dependency to the reactive graph and the list never updates on pill
	// click.
	$: filtered = entries.filter(e => {
		if (activeType === 'all') return true;
		if (activeType === 'maintenance') return !NAMED_TYPES.has(e.type);
		const wanted = CATEGORY_MAP[activeType] || [];
		return wanted.includes(e.type);
	}).slice(0, 200);
	$: grouped = groupEntries(filtered);

	function toggleGroup(filename) {
		expandedGroup = expandedGroup === filename ? null : filename;
	}

	function groupStatus(entries) {
		// Most recent entry drives the status — entries are newest-first from API
		return entries[0]?.status || 'ok';
	}

	// Compute date range boundaries for API query.
	// `all` sends a far-past startDate explicitly — without it, the backend
	// defaults to a 24h window when no startDate is supplied, which would make
	// "all" silently behave as "24h".
	function dateParams(range) {
		const now = new Date();
		let start;
		if (range === '1d') start = new Date(now.getTime() - 86400000);
		else if (range === '7d') start = new Date(now.getTime() - 7 * 86400000);
		else if (range === '30d') start = new Date(now.getTime() - 30 * 86400000);
		else if (range === 'all') start = new Date('2020-01-01T00:00:00Z');
		else return {};
		return { startDate: start.toISOString() };
	}

	async function loadLog() {
		loading = true;
		try {
			// Server filters by date only — category filtering happens client-side
			// against `matchesActiveType` so "maintenance" can use a single
			// negative-set match without round-trips per type.
			const params = { ...dateParams(dateRange) };
			const rows = await fetchLog(params);
			entries = rows.map(r => ({
				id: r.id,
				timestamp: r.created_at,
				type: r.type,
				status: r.status,
				summary: r.summary,
				data: r.data || {}
			}));
		} catch (err) {
			entries = [];
		}
		loading = false;
	}

	// Re-fetch when date range changes; activeType is purely client-side
	$: dateRange, loadLog();

	function toggleRow(id) {
		expandedId = expandedId === id ? null : id;
	}

	function toggleTechnical(id) {
		showTechnicalById = { ...showTechnicalById, [id]: !showTechnicalById[id] };
	}

	function formatTs(dateStr) {
		const d = new Date(dateStr);
		const y = d.getFullYear();
		const mo = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		const h = String(d.getHours()).padStart(2, '0');
		const m = String(d.getMinutes()).padStart(2, '0');
		const s = String(d.getSeconds()).padStart(2, '0');
		return `${y}-${mo}-${day} ${h}:${m}:${s}`;
	}

	// Phase-strip duration formatter. Sub-second → "Xms"; otherwise "X.Xs".
	function formatDuration(ms) {
		if (ms == null) return '—';
		if (ms < 1000) return `${Math.round(ms)}ms`;
		return `${(ms / 1000).toFixed(1)}s`;
	}

	function explorerLink(nodeId) {
		if (!nodeId) return null;
		return `#/explorer?node=${encodeURIComponent(nodeId)}`;
	}

	function exportCsv() {
		const header = 'timestamp,type,status,summary,data';
		const rows = filtered.map(e => {
			const ts = formatTs(e.timestamp);
			const dataStr = JSON.stringify(e.data).replace(/"/g, '""');
			const summ = e.summary.replace(/"/g, '""');
			return `${ts},${e.type},${e.status},"${summ}","${dataStr}"`;
		});
		const csv = header + '\n' + rows.join('\n');
		const blob = new Blob([csv], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `adrasi-log-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}
</script>

<Navbar {navigate} active="ops" />

<div class="log-page">
	<div class="toolbar">
		<div class="pill-group">
			{#each TYPES as t}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<span class="pill" class:pill-active={activeType === t} on:click={() => activeType = t}>{t}</span>
			{/each}
		</div>
		<div class="toolbar-right">
			<div class="pill-group">
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<span class="pill" class:pill-active={dateRange === '1d'} on:click={() => dateRange = '1d'}>24h</span>
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<span class="pill" class:pill-active={dateRange === '7d'} on:click={() => dateRange = '7d'}>7d</span>
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<span class="pill" class:pill-active={dateRange === '30d'} on:click={() => dateRange = '30d'}>30d</span>
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<span class="pill" class:pill-active={dateRange === 'all'} on:click={() => dateRange = 'all'}>all</span>
			</div>
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<span class="export-btn" on:click={exportCsv}>
				<Download size={12} strokeWidth={1.5} />
				CSV
			</span>
			<span class="entry-count">{filtered.length}</span>
		</div>
	</div>

	<div class="log-scroll">
		{#each grouped as item}
			{#if item.type === 'single'}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<div class="log-row" class:row-error={item.entry.status === 'error'} class:row-expanded={expandedId === item.entry.id} on:click={() => toggleRow(item.entry.id)}>
					<span class="col-chevron">{expandedId === item.entry.id ? '▾' : '▸'}</span>
					<span class="col-ts">{formatTs(item.entry.timestamp)}</span>
					<span class="col-status" class:s-ok={item.entry.status === 'ok'} class:s-err={item.entry.status === 'error'} class:s-warn={item.entry.status === 'warn'}>{item.entry.status === 'ok' ? 'OK' : item.entry.status === 'warn' ? 'WARN' : 'ERR'}</span>
					<span class="col-type">{categoryFor(item.entry.type)}</span>
					<span class="col-summary">{item.entry.summary}</span>
				</div>
				{#if expandedId === item.entry.id}
					{@const d = item.entry.data || {}}
					<div class="log-detail">
						<div class="detail-grid">
							{#if d.sender}
								<div class="detail-row"><span class="detail-key">From</span><span class="detail-val">{d.sender}</span></div>
							{/if}
							{#if d.user_email}
								<div class="detail-row"><span class="detail-key">User</span><span class="detail-val">{d.user_email}</span></div>
							{/if}
							{#if d.subject}
								<div class="detail-row"><span class="detail-key">Subject</span><span class="detail-val">{d.subject}</span></div>
							{/if}
							{#if d.title}
								<div class="detail-row"><span class="detail-key">Title</span><span class="detail-val">{d.title}</span></div>
							{/if}
							{#if d.root_concept}
								<div class="detail-row"><span class="detail-key">Concept</span>
									{#if d.root_concept_id}
										<a class="detail-link" href={explorerLink(d.root_concept_id)}>{d.root_concept}</a>
									{:else}
										<span class="detail-val">{d.root_concept}</span>
									{/if}
								</div>
							{:else if d.project}
								<div class="detail-row"><span class="detail-key">Project</span><span class="detail-val">{d.project}</span></div>
							{/if}
							{#if Array.isArray(d.file_names) && d.file_names.length > 0}
								<div class="detail-row"><span class="detail-key">Files</span><span class="detail-val">{d.file_names.join(', ')} <span class="detail-meta">({d.file_count} {d.file_count === 1 ? 'file' : 'files'})</span></span></div>
							{:else if d.filename}
								<div class="detail-row"><span class="detail-key">File</span><span class="detail-val">{d.filename}</span></div>
							{/if}
							{#if Array.isArray(d.doc_titles) && d.doc_titles.length > 0}
								<div class="detail-row"><span class="detail-key">Documents</span><span class="detail-val">{d.doc_titles.join(', ')}</span></div>
							{/if}
							{#if d.documents != null || d.documents_updated != null || d.documents_created != null}
								<div class="detail-row"><span class="detail-key">Doc results</span><span class="detail-val">
									{(d.documents ?? d.documents_created ?? 0)} created{d.documents_updated != null ? `, ${d.documents_updated} updated` : ''}{d.errors ? `, ${d.errors} failed` : ''}
								</span></div>
							{/if}
							{#if d.concepts_created != null || d.concepts_merged != null}
								<div class="detail-row"><span class="detail-key">Concept results</span><span class="detail-val">{d.concepts_created || 0} created, {d.concepts_merged || 0} merged</span></div>
							{/if}
							{#if item.entry.type === 'mind' && d.event_type}
								<div class="detail-row"><span class="detail-key">Event</span><span class="detail-val">{d.event_type}{d.fast_path === false ? ' (full pipeline)' : ' (fast path)'}</span></div>
							{/if}
							{#if d.duration_s}
								<div class="detail-row"><span class="detail-key">Total time</span><span class="detail-val">{d.duration_s}s</span></div>
							{:else if d.duration_ms}
								<div class="detail-row"><span class="detail-key">Total time</span><span class="detail-val">{formatDuration(d.duration_ms)}</span></div>
							{/if}
							{#if d.error}
								<div class="detail-row detail-error"><span class="detail-key">Error</span><span class="detail-val">{d.error}</span></div>
							{/if}
						</div>

						{#if d.phase_durations_ms}
							<div class="phase-strip">
								{#each Object.entries(d.phase_durations_ms) as [phase, ms]}
									<div class="phase-cell">
										<div class="phase-label">{PHASE_LABELS[phase] || phase}</div>
										<div class="phase-duration">{formatDuration(ms)}</div>
									</div>
								{/each}
							</div>
						{/if}

						<!-- svelte-ignore a11y-click-events-have-key-events -->
						<span class="tech-toggle" on:click|stopPropagation={() => toggleTechnical(item.entry.id)}>
							{showTechnicalById[item.entry.id] ? '− Hide technical' : '+ Show technical'}
						</span>
						{#if showTechnicalById[item.entry.id]}
							<pre class="detail-json">{JSON.stringify(item.entry.data, null, 2)}</pre>
						{/if}
					</div>
				{/if}
			{:else}
				<!-- Group row: most recent entry drives status -->
				{@const head = item.entries[0]}
				{@const status = groupStatus(item.entries)}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<div class="log-row" class:row-error={status === 'error'} class:row-expanded={expandedGroup === item.filename} on:click={() => toggleGroup(item.filename)}>
					<span class="col-chevron">{expandedGroup === item.filename ? '▾' : '▸'}</span>
					<span class="col-ts">{formatTs(head.timestamp)}</span>
					<span class="col-status" class:s-ok={status === 'ok'} class:s-err={status === 'error'} class:s-warn={status === 'warn'}>{status === 'ok' ? 'OK' : status === 'warn' ? 'WARN' : 'ERR'}</span>
					<span class="col-type">Upload</span>
					<span class="col-summary">{head.summary}</span>
					{#if item.entries.length > 1}<span class="col-count">{item.entries.length}</span>{/if}
				</div>
				{#if expandedGroup === item.filename}
					<div class="log-group-detail">
						{#each item.entries as entry (entry.id)}
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<div class="log-subrow" class:subrow-error={entry.status === 'error'} on:click|stopPropagation={() => toggleRow(entry.id)}>
								<span class="col-chevron sub-chevron">{expandedId === entry.id ? '▾' : '▸'}</span>
								<span class="col-ts sub-ts">{formatTs(entry.timestamp)}</span>
								<span class="col-status" class:s-ok={entry.status === 'ok'} class:s-err={entry.status === 'error'} class:s-warn={entry.status === 'warn'}>{entry.status === 'ok' ? 'OK' : entry.status === 'warn' ? 'WARN' : 'ERR'}</span>
								<span class="col-summary">{entry.summary}</span>
							</div>
							{#if expandedId === entry.id}
								{@const d = entry.data || {}}
								<div class="log-detail log-subdetail">
									<div class="detail-grid">
										{#if d.sender}
											<div class="detail-row"><span class="detail-key">From</span><span class="detail-val">{d.sender}</span></div>
										{/if}
										{#if d.user_email}
											<div class="detail-row"><span class="detail-key">User</span><span class="detail-val">{d.user_email}</span></div>
										{/if}
										{#if d.root_concept}
											<div class="detail-row"><span class="detail-key">Concept</span>
												{#if d.root_concept_id}
													<a class="detail-link" href={explorerLink(d.root_concept_id)}>{d.root_concept}</a>
												{:else}
													<span class="detail-val">{d.root_concept}</span>
												{/if}
											</div>
										{:else if d.project}
											<div class="detail-row"><span class="detail-key">Project</span><span class="detail-val">{d.project}</span></div>
										{/if}
										{#if d.documents != null || d.documents_updated != null || d.documents_created != null}
											<div class="detail-row"><span class="detail-key">Doc results</span><span class="detail-val">
												{(d.documents ?? d.documents_created ?? 0)} created{d.documents_updated != null ? `, ${d.documents_updated} updated` : ''}{d.errors ? `, ${d.errors} failed` : ''}
											</span></div>
										{/if}
										{#if d.concepts_created != null || d.concepts_merged != null}
											<div class="detail-row"><span class="detail-key">Concepts</span><span class="detail-val">{d.concepts_created || 0} created, {d.concepts_merged || 0} merged</span></div>
										{/if}
										{#if d.error}
											<div class="detail-row detail-error"><span class="detail-key">Error</span><span class="detail-val">{d.error}</span></div>
										{/if}
									</div>

									{#if d.phase_durations_ms}
										<div class="phase-strip">
											{#each Object.entries(d.phase_durations_ms) as [phase, ms]}
												<div class="phase-cell">
													<div class="phase-label">{PHASE_LABELS[phase] || phase}</div>
													<div class="phase-duration">{formatDuration(ms)}</div>
												</div>
											{/each}
										</div>
									{/if}

									<!-- svelte-ignore a11y-click-events-have-key-events -->
									<span class="tech-toggle" on:click|stopPropagation={() => toggleTechnical(entry.id)}>
										{showTechnicalById[entry.id] ? '− Hide technical' : '+ Show technical'}
									</span>
									{#if showTechnicalById[entry.id]}
										<pre class="detail-json">{JSON.stringify(entry.data, null, 2)}</pre>
									{/if}
								</div>
							{/if}
						{/each}
					</div>
				{/if}
			{/if}
		{/each}
		{#if loading}
			<div class="log-empty">Loading...</div>
		{:else if grouped.length === 0}
			<div class="log-empty">No matching entries.</div>
		{/if}
	</div>
</div>

<style>
	.log-page {
		min-height: 100vh;
		background: #F9FAFB;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		-webkit-font-smoothing: antialiased;
		display: flex;
		flex-direction: column;
	}

	/* ================================================================
	   TOOLBAR
	   ================================================================ */

	.toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 20px;
		background: #FFFFFF;
		border-bottom: 1px solid #E5E7EB;
		flex-shrink: 0;
		gap: 12px;
	}

	.toolbar-right {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.pill-group {
		display: flex;
		gap: 1px;
		background: #E5E7EB;
		border-radius: 6px;
		padding: 2px;
	}

	.pill {
		font-size: 11px;
		font-weight: 500;
		color: #6B7280;
		padding: 4px 10px;
		border-radius: 4px;
		cursor: pointer;
		transition: all 100ms ease-out;
		user-select: none;
		text-transform: capitalize;
	}

	.pill:hover { color: #374151; }

	.pill-active {
		background: #FFFFFF;
		color: #111827;
		font-weight: 600;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
	}

	.export-btn {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 11px;
		font-weight: 500;
		color: #6B7280;
		cursor: pointer;
		padding: 4px 10px;
		border-radius: 4px;
		border: 1px solid #E5E7EB;
		transition: all 100ms ease-out;
		user-select: none;
	}

	.export-btn:hover { color: #111827; border-color: #D1D5DB; background: #F9FAFB; }

	.entry-count {
		font-size: 11px;
		font-weight: 500;
		color: #9CA3AF;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		min-width: 24px;
		text-align: right;
	}

	/* ================================================================
	   LOG
	   ================================================================ */

	.log-scroll {
		flex: 1;
		overflow-y: auto;
	}

	.log-row {
		display: flex;
		align-items: baseline;
		padding: 6px 20px;
		border-bottom: 1px solid #F3F4F6;
		cursor: pointer;
		transition: background 60ms ease-out;
		gap: 0;
	}

	.log-row:hover { background: #F3F4F6; }
	.row-expanded { background: #F9FAFB; }
	.row-error { color: #DC2626; }
	.row-error .col-summary { color: #DC2626; }

	.col-chevron {
		width: 16px;
		flex-shrink: 0;
		font-size: 10px;
		color: #9CA3AF;
		line-height: 1;
	}

	.col-ts {
		width: 140px;
		flex-shrink: 0;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 11px;
		color: #9CA3AF;
	}

	.col-status {
		width: 44px;
		flex-shrink: 0;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.03em;
	}

	.s-ok { color: #10B981; }
	.s-err { color: #DC2626; }
	.s-warn { color: #D97706; }

	.col-type {
		width: 96px;
		flex-shrink: 0;
		font-size: 11px;
		color: #6B7280;
	}

	.col-summary {
		flex: 1;
		font-size: 12px;
		color: #374151;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
	}

	/* ================================================================
	   DETAIL PANE
	   ================================================================ */

	.log-detail {
		padding: 10px 20px 12px 36px;
		background: #F9FAFB;
		border-bottom: 1px solid #E5E7EB;
	}

	.detail-grid {
		display: grid;
		grid-template-columns: 120px 1fr;
		row-gap: 4px;
		column-gap: 12px;
		margin-bottom: 10px;
	}

	.detail-row {
		display: contents;
	}

	.detail-key {
		font-size: 11px;
		color: #9CA3AF;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.detail-val {
		font-size: 12px;
		color: #374151;
		line-height: 1.5;
		word-break: break-word;
	}

	.detail-meta {
		color: #9CA3AF;
		font-size: 11px;
	}

	.detail-link {
		font-size: 12px;
		color: #1F2937;
		text-decoration: none;
		border-bottom: 1px solid #D1D5DB;
		transition: border-color 100ms ease-out;
	}

	.detail-link:hover {
		border-bottom-color: #1F2937;
	}

	.detail-error .detail-val {
		color: #DC2626;
	}

	.phase-strip {
		display: flex;
		gap: 1px;
		background: #E5E7EB;
		border: 1px solid #E5E7EB;
		border-radius: 4px;
		overflow: hidden;
		margin: 8px 0 12px;
	}

	.phase-cell {
		flex: 1;
		background: #FFFFFF;
		padding: 6px 10px;
		min-width: 0;
	}

	.phase-label {
		font-size: 10px;
		font-weight: 500;
		color: #6B7280;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		margin-bottom: 2px;
	}

	.phase-duration {
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 12px;
		font-weight: 600;
		color: #111827;
	}

	.tech-toggle {
		display: inline-block;
		font-size: 10px;
		font-weight: 500;
		color: #9CA3AF;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		cursor: pointer;
		padding: 2px 6px;
		border-radius: 3px;
		user-select: none;
		transition: color 100ms ease-out;
	}

	.tech-toggle:hover {
		color: #374151;
	}

	.detail-json {
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 11px;
		line-height: 1.6;
		color: #374151;
		margin: 6px 0 0;
		padding: 10px 14px;
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 4px;
		overflow-x: auto;
		white-space: pre;
	}

	.col-count {
		margin-left: auto;
		margin-right: 8px;
		font-size: 10px;
		font-weight: 600;
		color: #9CA3AF;
		background: #F3F4F6;
		border: 1px solid #E5E7EB;
		border-radius: 10px;
		padding: 1px 7px;
		flex-shrink: 0;
	}

	.log-group-detail {
		border-bottom: 1px solid #E5E7EB;
		background: #F9FAFB;
	}

	.log-subrow {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 6px 20px 6px 36px;
		cursor: pointer;
		border-top: 1px solid #F3F4F6;
		font-size: 12px;
		color: #4B5563;
	}
	.log-subrow:hover { background: #F3F4F6; }
	.subrow-error .col-summary { color: #DC2626; }

	.sub-chevron { font-size: 8px; color: #9CA3AF; flex-shrink: 0; }
	.sub-ts { width: 140px; flex-shrink: 0; font-size: 11px; color: #9CA3AF; }

	.log-subdetail {
		padding-left: 56px;
	}

	.log-empty {
		padding: 48px 24px;
		text-align: center;
		font-size: 12px;
		color: #9CA3AF;
	}

	@media (max-width: 768px) {
		.col-ts { width: 90px; font-size: 10px; }
		.toolbar {
			flex-direction: column;
			align-items: stretch;
			gap: 10px;
			padding: 12px 14px;
		}
		.pill-group { flex-wrap: wrap; }
		.toolbar-right { flex-wrap: wrap; width: 100%; gap: 8px; }
		.log-detail { padding: 10px 12px 12px 12px; }
		.detail-grid { grid-template-columns: 80px 1fr; }
		.phase-strip { flex-wrap: wrap; }
		.phase-cell { flex-basis: calc(50% - 1px); }
		/* Bigger tap targets on the filter pills */
		.pill {
			padding: 8px 12px;
			font-size: 12px;
			min-height: 36px;
			display: inline-flex;
			align-items: center;
			white-space: nowrap;
		}
		.export-btn { min-height: 36px; padding: 8px 12px; }
	}

	@media (max-width: 480px) {
		.col-type { display: none; }
		.col-ts { width: 64px; font-size: 9px; }
	}
</style>
